import React, { useState } from 'react';
import { diagnose } from '../api/client';
import { usePermissions } from '../contexts/PermissionContext';
import { Bot, Zap, AlertCircle } from 'lucide-react';

const EQUIPMENT_TYPES = ['HVAC', 'Commercial Refrigerator', 'Commercial Freezer', 'Ice Machine', 'Walk-in Cooler', 'Walk-in Freezer', 'Kitchen Equipment', 'Appliance'];

export default function AIAssistant() {
  const { hasPermission } = usePermissions();
  const canDiagnose = hasPermission('ai_assistant:diagnose');

  const [form, setForm] = useState({ equipment_type: '', equipment_brand: '', problem_description: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await diagnose(form);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get AI diagnosis');
    } finally {
      setLoading(false);
    }
  };

  const formatSuggestions = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('##') || line.startsWith('**')) {
        return <p key={i} style={{ fontWeight: 700, color: '#1E3A8A', marginTop: 16, marginBottom: 6, fontSize: 14 }}>{line.replace(/\*\*/g, '').replace(/##/g, '').trim()}</p>;
      }
      if (line.startsWith('- ') || line.match(/^\d+\./)) {
        return <li key={i} style={{ marginLeft: 20, marginBottom: 4, fontSize: 13, color: '#374151' }}>{line.replace(/^- /, '').replace(/^\d+\. /, '')}</li>;
      }
      if (line.trim()) {
        return <p key={i} style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>{line}</p>;
      }
      return <br key={i} />;
    });
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{ background: '#EFF6FF', borderRadius: 10, padding: 10 }}>
          <Bot size={24} color="#2563EB" />
        </div>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>AI Repair Assistant</h1>
          <p style={{ color: '#6B7280', fontSize: 14 }}>Powered by GPT-4 — describe the issue and get expert troubleshooting guidance</p>
        </div>
      </div>

      {/* Form */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, marginTop: 20, border: '1px solid #E5E7EB' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Equipment Type *</label>
              <select required value={form.equipment_type} onChange={e => setForm({ ...form, equipment_type: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }}>
                <option value="">Select equipment type</option>
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Brand / Model (optional)</label>
              <input value={form.equipment_brand} onChange={e => setForm({ ...form, equipment_brand: e.target.value })}
                placeholder="e.g. Atosa, True, Hoshizaki..."
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }} />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Problem Description *</label>
            <textarea required rows={4} value={form.problem_description}
              onChange={e => setForm({ ...form, problem_description: e.target.value })}
              placeholder="Describe the issue in detail: symptoms, error codes, when it started, what has been checked..."
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, resize: 'vertical' }} />
          </div>

          {canDiagnose ? (
            <button type="submit" disabled={loading} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '12px 28px',
              background: loading ? '#93C5FD' : '#2563EB', color: '#fff',
              border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 15,
            }}>
              <Zap size={17} />
              {loading ? 'Analyzing...' : 'Get AI Diagnosis'}
            </button>
          ) : (
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#92400E' }}>
              You do not have permission to run AI diagnostics.
            </div>
          )}
        </form>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: 16, marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertCircle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ color: '#DC2626', fontSize: 14 }}>{error}</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 28, marginTop: 20, border: '1px solid #DBEAFE' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Bot size={18} color="#2563EB" />
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1E3A8A' }}>AI Diagnosis Report</h2>
            {result.model_used && result.model_used !== 'none' && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', background: '#F3F4F6', padding: '2px 8px', borderRadius: 20 }}>
                {result.model_used} · {result.tokens_used} tokens
              </span>
            )}
          </div>
          <div style={{ lineHeight: 1.7 }}>
            {formatSuggestions(result.suggestions)}
          </div>

          <div style={{ marginTop: 20, background: '#FEF3C7', borderRadius: 8, padding: 12, fontSize: 12, color: '#92400E' }}>
            <strong>Tip:</strong> Always verify diagnosis with physical inspection. Follow safety procedures before working on pressurized or electrical systems.
          </div>
        </div>
      )}

      {/* Example prompts */}
      {!result && !loading && canDiagnose && (
        <div style={{ marginTop: 24 }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 12 }}>Example issues to diagnose:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              'Not cooling, compressor running but warm inside',
              'Ice machine not making ice, water flowing normally',
              'Unit freezing over, frost buildup on evaporator',
              'Compressor short cycling every 5 minutes',
              'Unusual loud noise when compressor starts',
            ].map(example => (
              <button key={example} onClick={() => setForm({ ...form, problem_description: example })}
                style={{ padding: '6px 14px', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
